import { useState, useEffect, type ReactNode } from 'react';
import { Bookmark } from 'lucide-react';
import { api, type ChatChannelResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { seededGradient } from '../utils';
import { UserAvatar } from './UserAvatar';

interface ChannelAvatarProps {
  channel: ChatChannelResponse;
  /** Resolved channel label — seeds the gradient, the image alt, and the initial. */
  displayName: string;
  /** Size classes for the round avatar container (default `h-10 w-10`). */
  sizeClassName?: string;
  /** Text-size class for the fallback initial (default `text-sm`). */
  textClassName?: string;
  /** Bookmark glyph size for a self-DM (default `h-5 w-5`). */
  bookmarkClassName?: string;
  /**
   * Fallback shown for a NON-DM channel (group/guild/global, or a partnerless
   * direct channel) with no avatar image. The list uses the name's initial; the
   * header uses `#` for multi-member channels — so the glyph is caller-supplied.
   */
  fallbackGlyph: ReactNode;
}

/**
 * The avatar shown for a channel wherever it's listed — the left-panel list, the
 * header bar, previews. A one-to-one DM renders the partner's personalized user
 * avatar (keyed on their userId and presigned by the API), matching what the
 * message list renders; a self-DM shows the "Saved Messages" bookmark; and a
 * group/guild channel shows its own channel avatar (falling back to a
 * seeded-gradient glyph). Single source of truth so these surfaces never drift.
 */
export function ChannelAvatar({
  channel,
  displayName,
  sizeClassName = 'h-10 w-10',
  textClassName = 'text-sm',
  bookmarkClassName = 'h-5 w-5',
  fallbackGlyph,
}: ChannelAvatarProps) {
  const isSelfDm = channel.isSelfDm === true;
  const dmPartnerId =
    channel.channelType === 'direct' && !isSelfDm ? (channel.dmPartnerId ?? null) : null;

  // A DM partner renders their own personalized avatar — the channel-avatar
  // resolution below lives in a sibling component that isn't mounted here, so its
  // effect never runs for DMs.
  if (dmPartnerId) {
    return (
      <UserAvatar
        userId={dmPartnerId}
        displayName={displayName}
        initial={displayName.charAt(0).toUpperCase() || '?'}
        className={sizeClassName}
        fallbackClassName={cn(textClassName, 'font-medium text-white')}
        background={seededGradient(displayName)}
      />
    );
  }

  return (
    <ChannelImageAvatar
      channel={channel}
      displayName={displayName}
      isSelfDm={isSelfDm}
      sizeClassName={sizeClassName}
      textClassName={textClassName}
      bookmarkClassName={bookmarkClassName}
      fallbackGlyph={fallbackGlyph}
    />
  );
}

/**
 * The channel-owned avatar: a self-DM bookmark, an uploaded channel image, or a
 * seeded-gradient fallback glyph. A channel referencing a private `avatarFileId`
 * (rather than a public `imageRef`) is presigned on demand. Kept separate from
 * {@link ChannelAvatar} so this resolution only runs when a channel avatar is
 * actually rendered — never for a DM, which shows the partner's user avatar.
 */
function ChannelImageAvatar({
  channel,
  displayName,
  isSelfDm,
  sizeClassName,
  textClassName,
  bookmarkClassName,
  fallbackGlyph,
}: {
  channel: ChatChannelResponse;
  displayName: string;
  isSelfDm: boolean;
  sizeClassName: string;
  textClassName: string;
  bookmarkClassName: string;
  fallbackGlyph: ReactNode;
}) {
  const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState<string | null>(
    channel.imageRef ?? null,
  );

  useEffect(() => {
    setResolvedAvatarUrl(channel.imageRef ?? null);
    if (!channel.imageRef && channel.avatarFileId) {
      api
        .getDownloadUrl(channel.avatarFileId)
        .then((r) => setResolvedAvatarUrl(r.downloadUrl))
        .catch(() => {});
    }
  }, [channel.channelId, channel.imageRef, channel.avatarFileId]);

  return (
    <div
      className={cn(
        sizeClassName,
        'rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center',
      )}
      style={
        !resolvedAvatarUrl || isSelfDm ? { background: seededGradient(displayName) } : undefined
      }
    >
      {isSelfDm ? (
        <Bookmark className={cn(bookmarkClassName, 'text-white')} fill="currentColor" />
      ) : resolvedAvatarUrl ? (
        <img src={resolvedAvatarUrl} alt={displayName} className="w-full h-full object-cover" />
      ) : (
        <span className={cn(textClassName, 'font-medium text-white')}>{fallbackGlyph}</span>
      )}
    </div>
  );
}
